import { CheckCircle2, Instagram, Linkedin } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { ImageSplit } from "@/components/common/ImageSplit";
import { ClosingCTA } from "@/components/common/ClosingCTA";
import { Button } from "@/components/common/Button";
import { ABOUT, FOUNDER, ASSETS, LEGAL, IMAGES } from "@/constants";
import { Seo } from "@/components/common/Seo";
import SEO from "@/components/SEO";

export default function AboutPage() {
  return (
    <>
    <SEO page="about" />
    <div data-testid="about-page">
      <Seo title="About CET" path="/about" description="Learn about CET — a national corporate-readiness exam by 
       operated by Devobyte OPC Private Limited, building a merit-based bridge from Indian campuses to the corporate world." />
      <PageHero eyebrow={ABOUT.hero.eyebrow} title={ABOUT.hero.heading} subtitle={ABOUT.hero.body} image={IMAGES.capToss} />

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={ABOUT.mission.heading} subtitle={ABOUT.mission.body} />
        </div>
      </section>

      <ImageSplit
        reverse
        eyebrow="Student Success"
        title="Built for Every Graduate in India"
        body="Whether you're from a metro university or a small-town college, CET levels the playing field with one transparent, national benchmark."
        image={IMAGES.laptopStudy}
        imageAlt="Student preparing online"
        points={[
          "Open to all recognized colleges & universities",
          "Fully online — accessible from anywhere",
          "Real rank, real report, real employer access",
        ]}
      />

      <section className="bg-slate-50/60 py-16 sm:py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Difference" title="What Makes CET Different" align="center" />
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {ABOUT.different.map((d) => (
              <div
                key={d.title}
                className="rounded-xl border border-slate-200 bg-white p-6 hover:border-[#F5A623] transition-colors"
              >
                <CheckCircle2 className="h-6 w-6 text-[#F5A623]" />
                <h3 className="mt-4 font-heading font-bold text-lg text-slate-900">{d.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="relative bg-slate-950 text-white overflow-hidden py-16 sm:py-20">
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_25%_30%,#F5A623_0,transparent_45%)]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#F5A623] mb-3 text-center">
            {FOUNDER.eyebrow}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center">
            {FOUNDER.heading}
          </h2>
          <div className="mt-8 space-y-5 border-l-2 border-[#F5A623] pl-5 sm:pl-6">
            {FOUNDER.message.map((p, i) => (
              <p key={i} className="text-base sm:text-lg text-slate-200 leading-relaxed italic">
                {p}
              </p>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-2 text-center">
            {/* <img src={ASSETS.startupTimesLogo} alt="Startup Times" className="h-7 w-auto" /> */}
            <p className="font-semibold text-white">— {FOUNDER.name}</p>
            <p className="text-sm text-[#F5A623]">{FOUNDER.role}</p>
          </div>
        </div>
      </section>

      {/* Organized by */}
      <section className="bg-slate-950 text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* <img src={ASSETS.startupTimesLogo} alt="Startup Times" className="h-10 w-auto mx-auto" /> */}
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold">
  An Initiative by {" "}
  <span className="text-[#F5A623]">Startup</span>{" "}
  <span className="text-white">Times</span>
</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">{ABOUT.organizedBy.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button href={LEGAL.startupTimes.website} variant="ghostLight" data-testid="visit-startup-times-btn">
              Visit <span className="text-[#F5A623]">Startup</span>{" "}
    <span className="text-white">Times</span>
            </Button>
            <Button href={LEGAL.startupTimes.instagram} variant="ghostLight" data-testid="st-instagram-btn">
              <Instagram className="h-4 w-4" /> Instagram
            </Button>
            <Button href={LEGAL.startupTimes.linkedin} variant="ghostLight" data-testid="st-linkedin-btn">
              <Linkedin className="h-4 w-4" /> LinkedIn
            </Button>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
    </>
  );
}
